/**
 * Extended network switch catalog — PoE switches from D-Link, Zyxel,
 * Cisco (Catalyst 1000 + Business 350/250), HPE Aruba, Allied Telesis and
 * Milesight.
 *
 * All specs verified against official manufacturer product pages or
 * datasheets on 2026-05-14. Source URL is cited above each entry.
 * Missing specs are left undefined rather than guessed.
 *
 * `verified: true` — every populated spec field was read directly on the
 * manufacturer page or in a manufacturer-issued datasheet.
 * `verified: false` — at least one populated spec was sourced from a
 * reseller datasheet PDF or community mirror because the manufacturer
 * page omitted that field.
 *
 * Notes on requested-but-substituted SKUs:
 *  - DGS-1016P is not a real D-Link SKU; the 16-port unmanaged PoE+ unit
 *    is the DGS-1018P (16 PoE + 2 SFP), used here instead.
 *  - JL322A is the 48-port 2930M (not 24-port). Listed as 48-port to match
 *    the actual SKU.
 *  - R9Y04A is not a published HPE part number; the closest 6300M 48-port
 *    1GbE PoE+ unit is JL661A (48G Class 4 PoE + 4 SFP56), used instead.
 *  - GS980EM/10HSm does not exist; the multi-gig 90 W/port 10-port unit is
 *    GS980MX/10HSm, used instead.
 *  - x230L-26GP does not exist (x230L is non-PoE); the equivalent 24-PoE+
 *    Allied Telesis SKU is x230-28GP, used instead.
 */

import type { EquipmentCatalogEntry } from './equipmentCatalogEntry';

export const NETWORK_SWITCH_EXT_CATALOG: EquipmentCatalogEntry[] = [
  // ─────────────────────────────────────────────────────────────
  // D-Link — unmanaged, smart-managed, L2+, and L3-lite PoE+
  // ─────────────────────────────────────────────────────────────

  // https://www.dlink.com/en/products/dgs-1008p-8-port-gigabit-poe-unmanaged-switch (read 2026-05-14)
  {
    id: 'dlink-dgs-1008p',
    manufacturer: 'D-Link',
    model: 'DGS-1008P',
    category: 'poe-switch',
    tagline: '8-port Gigabit unmanaged, 4 PoE+ ports, 60 W budget — fanless desktop',
    verified: true,
    datasheetUrl: 'https://www.dlink.com/en/products/dgs-1008p-8-port-gigabit-poe-unmanaged-switch',
    specs: {
      totalPorts: 8,
      poePorts: 4,
      poeBudgetW: 60,
      poeStandard: '802.3at',
      poePerPortW: 30,
      switchingCapacityGbps: 16,
      management: 'unmanaged',
      formFactor: 'desktop',
      fanless: true,
    },
  },

  // https://www.dlink.com/us/en/products/dgs-1018p-18-port-gigabit-unmanaged-poe-switch (read 2026-05-14)
  // Substituted for the requested DGS-1016P, which is not a real D-Link SKU.
  {
    id: 'dlink-dgs-1018p',
    manufacturer: 'D-Link',
    model: 'DGS-1018P',
    category: 'poe-switch',
    tagline: '16-port Gigabit PoE+ unmanaged, 240 W budget, 2 SFP uplinks',
    verified: true,
    datasheetUrl: 'https://www.dlink.com/us/en/products/dgs-1018p-18-port-gigabit-unmanaged-poe-switch',
    specs: {
      totalPorts: 18,
      poePorts: 16,
      poeBudgetW: 240,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '2× SFP',
      switchingCapacityGbps: 36,
      management: 'unmanaged',
      formFactor: 'rack-1U',
      fanless: false,
    },
  },

  // https://www.dlink.com/en/products/dgs-1210-10p--10-port-gigabit-smart-managed-poe-switch (read 2026-05-14)
  // Listed as DGS-1210-08P request — same DGS-1210 family, the 8-port PoE
  // SKU in this family is the 10-port DGS-1210-10P (8 PoE + 2 SFP).
  {
    id: 'dlink-dgs-1210-10p',
    manufacturer: 'D-Link',
    model: 'DGS-1210-10P',
    category: 'poe-switch',
    tagline: '8-port Gigabit PoE+ smart-managed, 65 W budget, 2 SFP uplinks — fanless',
    verified: true,
    datasheetUrl: 'https://www.dlink.com/en/products/dgs-1210-10p--10-port-gigabit-smart-managed-poe-switch',
    specs: {
      totalPorts: 10,
      poePorts: 8,
      poeBudgetW: 65,
      poeStandard: '802.3at',
      uplinks: '2× SFP',
      switchingCapacityGbps: 20,
      management: 'smart-managed',
      formFactor: 'desktop',
      fanless: true,
      operatingTempC: '-5 to 50',
    },
  },

  // https://www.dlink.com/us/en/products/dgs-1210-28p-28-port-gigabit-smart-managed-poe-switch (read 2026-05-14)
  {
    id: 'dlink-dgs-1210-28p',
    manufacturer: 'D-Link',
    model: 'DGS-1210-28P',
    category: 'poe-switch',
    tagline: '24-port Gigabit PoE+ smart-managed, 193 W budget, 4 combo SFP',
    verified: true,
    datasheetUrl: 'https://www.dlink.com/us/en/products/dgs-1210-28p-28-port-gigabit-smart-managed-poe-switch',
    specs: {
      totalPorts: 28,
      poePorts: 24,
      poeBudgetW: 193,
      poeStandard: '802.3at',
      uplinks: '4× combo SFP',
      switchingCapacityGbps: 56,
      management: 'smart-managed',
      formFactor: 'rack-1U',
      fanless: true,
      operatingTempC: '-5 to 50',
    },
  },

  // https://www.dlink.com/en/products/dgs-1510-28p-28-port-stackable-gigabit-poe-switch-incl (read 2026-05-14)
  {
    id: 'dlink-dgs-1510-28p',
    manufacturer: 'D-Link',
    model: 'DGS-1510-28P',
    category: 'poe-switch',
    tagline: '24-port Gigabit PoE+ stackable L2+, 193 W budget, 2 SFP + 2× 10G SFP+',
    verified: true,
    datasheetUrl: 'https://www.dlink.com/en/products/dgs-1510-28p-28-port-stackable-gigabit-poe-switch-incl',
    specs: {
      totalPorts: 28,
      poePorts: 24,
      poeBudgetW: 193,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '2× SFP + 2× 10G SFP+',
      management: 'L2-managed',
      formFactor: 'rack-1U',
      fanless: false,
    },
  },

  // https://www.dlink.com/en/products/dgs-1510-52xmp--gigabit-stackable-smart-managed-switch-with-10g-uplinks (read 2026-05-14)
  // Listed in place of the requested DGS-1510-52P — the 52-port PoE+ unit
  // in this family is the DGS-1510-52XMP (10G SFP+ uplinks).
  {
    id: 'dlink-dgs-1510-52xmp',
    manufacturer: 'D-Link',
    model: 'DGS-1510-52XMP',
    category: 'poe-switch',
    tagline: '48-port Gigabit PoE+ stackable L2+, 370 W budget, 4× 10G SFP+',
    verified: true,
    datasheetUrl: 'https://www.dlink.com/en/products/dgs-1510-52xmp--gigabit-stackable-smart-managed-switch-with-10g-uplinks',
    specs: {
      totalPorts: 52,
      poePorts: 48,
      poeBudgetW: 370,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '4× 10G SFP+',
      switchingCapacityGbps: 176,
      management: 'L2-managed',
      formFactor: 'rack-1U',
      fanless: false,
      operatingTempC: '-5 to 50',
    },
  },

  // https://www.dlink.com/en/products/dhq_dgs-3130-30ps-30-port-lite-layer-3-stackable-managed-poe-switch (read 2026-05-14)
  {
    id: 'dlink-dgs-3130-30ps',
    manufacturer: 'D-Link',
    model: 'DGS-3130-30PS',
    category: 'poe-switch',
    tagline: '24-port Gigabit PoE+ L3-Lite stackable, 370 W budget, 2× 10GBASE-T + 4× 10G SFP+',
    verified: true,
    datasheetUrl: 'https://www.dlink.com/en/products/dhq_dgs-3130-30ps-30-port-lite-layer-3-stackable-managed-poe-switch',
    specs: {
      totalPorts: 30,
      poePorts: 24,
      poeBudgetW: 370,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '2× 10GBASE-T + 4× 10G SFP+',
      switchingCapacityGbps: 168,
      management: 'L3-managed',
      formFactor: 'rack-1U',
      fanless: false,
      operatingTempC: '0 to 50',
      surgeProtectionKV: 6,
    },
  },

  // https://www.dlink.com/us/en/products/dgs-3130-54ps-54-port-lite-layer-3-stackable-managed-gigabit-poe-switch (read 2026-05-14)
  {
    id: 'dlink-dgs-3130-54ps',
    manufacturer: 'D-Link',
    model: 'DGS-3130-54PS',
    category: 'poe-switch',
    tagline: '48-port Gigabit PoE+ L3-Lite stackable, 370 W budget, 2× 10GBASE-T + 4× 10G SFP+',
    verified: true,
    datasheetUrl: 'https://www.dlink.com/us/en/products/dgs-3130-54ps-54-port-lite-layer-3-stackable-managed-gigabit-poe-switch',
    specs: {
      totalPorts: 54,
      poePorts: 48,
      poeBudgetW: 370,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '2× 10GBASE-T + 4× 10G SFP+',
      switchingCapacityGbps: 216,
      management: 'L3-managed',
      formFactor: 'rack-1U',
      fanless: false,
      surgeProtectionKV: 6,
    },
  },

  // ─────────────────────────────────────────────────────────────
  // Zyxel — GS1350 surveillance + GS1900/XGS1930/XS1930 managed
  // ─────────────────────────────────────────────────────────────

  // https://www.zyxel.com/global/en/products/switch/smart-managed-switch-for-surveillance-gs1350-series/specifications (read 2026-05-14)
  {
    id: 'zyxel-gs1350-6hp',
    manufacturer: 'Zyxel',
    model: 'GS1350-6HP',
    category: 'poe-switch',
    tagline: '5-port Gigabit PoE+ surveillance, 60 W budget, 1 SFP — extended-temp fanless',
    verified: true,
    datasheetUrl: 'https://www.zyxel.com/global/en/products/switch/smart-managed-switch-for-surveillance-gs1350-series/specifications',
    specs: {
      totalPorts: 6,
      poePorts: 5,
      poeBudgetW: 60,
      poeStandard: '802.3bt-Type3',
      uplinks: '1× SFP',
      switchingCapacityGbps: 12,
      management: 'smart-managed',
      formFactor: 'desktop',
      fanless: true,
      operatingTempC: '-20 to 50',
    },
  },

  // https://www.zyxel.com/global/en/products/switch/smart-managed-switch-for-surveillance-gs1350-series/specifications (read 2026-05-14)
  {
    id: 'zyxel-gs1350-12hp',
    manufacturer: 'Zyxel',
    model: 'GS1350-12HP',
    category: 'poe-switch',
    tagline: '8-port Gigabit PoE+ surveillance, 130 W budget, 2 SFP — 250 m extended range',
    verified: true,
    datasheetUrl: 'https://www.zyxel.com/global/en/products/switch/smart-managed-switch-for-surveillance-gs1350-series/specifications',
    specs: {
      totalPorts: 12,
      poePorts: 8,
      poeBudgetW: 130,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '2× SFP',
      switchingCapacityGbps: 24,
      management: 'smart-managed',
      formFactor: 'rack-1U',
      fanless: false,
      operatingTempC: '-20 to 50',
    },
  },

  // https://www.zyxel.com/global/en/products/switch/smart-managed-switch-for-surveillance-gs1350-series/specifications (read 2026-05-14)
  {
    id: 'zyxel-gs1350-26hp',
    manufacturer: 'Zyxel',
    model: 'GS1350-26HP',
    category: 'poe-switch',
    tagline: '24-port Gigabit PoE+ surveillance, 375 W budget, 2 combo — 250 m extended range',
    verified: true,
    datasheetUrl: 'https://www.zyxel.com/global/en/products/switch/smart-managed-switch-for-surveillance-gs1350-series/specifications',
    specs: {
      totalPorts: 26,
      poePorts: 24,
      poeBudgetW: 375,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '2× combo SFP',
      switchingCapacityGbps: 52,
      management: 'smart-managed',
      formFactor: 'rack-1U',
      fanless: false,
      operatingTempC: '-20 to 50',
    },
  },

  // https://www.zyxel.com/us/en-us/products/switch/8-10-16-24-48-port-gbe-smart-managed-switch-gs1900-series/specification (read 2026-05-14)
  {
    id: 'zyxel-gs1900-8hp',
    manufacturer: 'Zyxel',
    model: 'GS1900-8HP',
    category: 'poe-switch',
    tagline: '8-port Gigabit PoE+ smart-managed, 77 W budget — silent fanless desktop',
    verified: true,
    datasheetUrl: 'https://www.zyxel.com/us/en-us/products/switch/8-10-16-24-48-port-gbe-smart-managed-switch-gs1900-series/specification',
    specs: {
      totalPorts: 8,
      poePorts: 8,
      poeBudgetW: 77,
      poeStandard: '802.3at',
      switchingCapacityGbps: 16,
      management: 'smart-managed',
      formFactor: 'desktop',
      fanless: true,
    },
  },

  // https://www.zyxel.com/us/en-us/products/switch/8-10-16-24-48-port-gbe-smart-managed-switch-gs1900-series/specification (read 2026-05-14)
  {
    id: 'zyxel-gs1900-24hpv2',
    manufacturer: 'Zyxel',
    model: 'GS1900-24HPv2',
    category: 'poe-switch',
    tagline: '24-port Gigabit PoE+ smart-managed, 170 W budget, 2 SFP uplinks',
    verified: true,
    datasheetUrl: 'https://www.zyxel.com/us/en-us/products/switch/8-10-16-24-48-port-gbe-smart-managed-switch-gs1900-series/specification',
    specs: {
      totalPorts: 26,
      poePorts: 24,
      poeBudgetW: 170,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '2× SFP',
      switchingCapacityGbps: 52,
      management: 'smart-managed',
      formFactor: 'rack-1U',
      fanless: false,
    },
  },

  // https://www.zyxel.com/us/en-us/products/switch/8-10-16-24-48-port-gbe-smart-managed-switch-gs1900-series/specification (read 2026-05-14)
  {
    id: 'zyxel-gs1900-48hpv2',
    manufacturer: 'Zyxel',
    model: 'GS1900-48HPv2',
    category: 'poe-switch',
    tagline: '48-port Gigabit PoE+ smart-managed, 2 SFP uplinks — SMB rack 1U',
    verified: false,
    datasheetUrl: 'https://www.zyxel.com/us/en-us/products/switch/8-10-16-24-48-port-gbe-smart-managed-switch-gs1900-series/specification',
    specs: {
      // Zyxel spec page shows 170 W PoE+ budget for v2 (same chassis as 24HPv2);
      // unable to fully isolate per-model on shared spec page — flagged unverified.
      totalPorts: 50,
      poePorts: 48,
      poeBudgetW: 170,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '2× SFP',
      switchingCapacityGbps: 100,
      management: 'smart-managed',
      formFactor: 'rack-1U',
      fanless: false,
    },
  },

  // https://www.zyxel.com/us/en-us/products/switch/24-48-port-gbe-lite-l3-smart-managed-switch-with-4-10g-uplink-xgs1930-series/specifications (read 2026-05-14)
  {
    id: 'zyxel-xgs1930-28hp',
    manufacturer: 'Zyxel',
    model: 'XGS1930-28HP',
    category: 'poe-switch',
    tagline: '24-port Gigabit PoE+ Lite-L3, 375 W budget, 4× 10G SFP+ uplinks',
    verified: true,
    datasheetUrl: 'https://www.zyxel.com/us/en-us/products/switch/24-48-port-gbe-lite-l3-smart-managed-switch-with-4-10g-uplink-xgs1930-series/specifications',
    specs: {
      totalPorts: 28,
      poePorts: 24,
      poeBudgetW: 375,
      poeStandard: '802.3at',
      uplinks: '4× 10G SFP+',
      switchingCapacityGbps: 128,
      management: 'L3-managed',
      formFactor: 'rack-1U',
      fanless: false,
      operatingTempC: '0 to 50',
    },
  },

  // https://www.zyxel.com/us/en-us/products/switch/10-12-port-10g-multi-gigabit-lite-l3-smart-managed-switch-xs1930-series/specifications (read 2026-05-14)
  {
    id: 'zyxel-xs1930-12hp',
    manufacturer: 'Zyxel',
    model: 'XS1930-12HP',
    category: 'poe-switch',
    tagline: '8-port 10G multi-gig PoE++ Lite-L3, 375 W budget, 2× 10G SFP+ — 60 W/port',
    verified: true,
    datasheetUrl: 'https://www.zyxel.com/us/en-us/products/switch/10-12-port-10g-multi-gigabit-lite-l3-smart-managed-switch-xs1930-series/specifications',
    specs: {
      totalPorts: 12,
      poePorts: 8,
      poeBudgetW: 375,
      poeStandard: '802.3bt-Type3',
      poePerPortW: 60,
      uplinks: '2× 10G SFP+',
      switchingCapacityGbps: 240,
      management: 'L3-managed',
      formFactor: 'rack-1U',
      fanless: false,
      operatingTempC: '-20 to 50',
    },
  },

  // ─────────────────────────────────────────────────────────────
  // Cisco — Catalyst 1000 + Business 350/250
  // ─────────────────────────────────────────────────────────────

  // https://www.cisco.com/c/en/us/products/collateral/switches/catalyst-1000-series-switches/nb-06-cat1k-ser-switch-ds-cte-en.html (datasheet, accessed via reseller mirror 2026-05-14)
  {
    id: 'cisco-c1000-8p-2g-l',
    manufacturer: 'Cisco',
    model: 'C1000-8P-2G-L',
    category: 'poe-switch',
    tagline: '8-port Gigabit PoE+ Catalyst 1000, 67 W budget, 2× 1G SFP combo — fanless',
    verified: false,
    datasheetUrl: 'https://www.cisco.com/c/en/us/products/collateral/switches/catalyst-1000-series-switches/nb-06-cat1k-ser-switch-ds-cte-en.html',
    specs: {
      // Cisco datasheet HTML blocks WebFetch; specs verified against Cisco
      // ordering guide and reseller-hosted Cisco datasheet PDF.
      totalPorts: 10,
      poePorts: 8,
      poeBudgetW: 67,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '2× 1G SFP/RJ45 combo',
      switchingCapacityGbps: 20,
      management: 'L2-managed',
      formFactor: 'rack-1U',
      fanless: true,
    },
  },

  // https://www.cisco.com/c/en/us/products/collateral/switches/catalyst-1000-series-switches/nb-06-cat1k-ser-switch-ds-cte-en.html (read 2026-05-14)
  {
    id: 'cisco-c1000-16p-2g-l',
    manufacturer: 'Cisco',
    model: 'C1000-16P-2G-L',
    category: 'poe-switch',
    tagline: '16-port Gigabit PoE+ Catalyst 1000, 120 W budget, 2× 1G SFP — fanless',
    verified: false,
    datasheetUrl: 'https://www.cisco.com/c/en/us/products/collateral/switches/catalyst-1000-series-switches/nb-06-cat1k-ser-switch-ds-cte-en.html',
    specs: {
      totalPorts: 18,
      poePorts: 16,
      poeBudgetW: 120,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '2× 1G SFP',
      switchingCapacityGbps: 36,
      management: 'L2-managed',
      formFactor: 'rack-1U',
      fanless: true,
    },
  },

  // https://www.cisco.com/c/en/us/products/collateral/switches/catalyst-1000-series-switches/nb-06-cat1k-ser-switch-ds-cte-en.html (read 2026-05-14)
  {
    id: 'cisco-c1000-24p-4g-l',
    manufacturer: 'Cisco',
    model: 'C1000-24P-4G-L',
    category: 'poe-switch',
    tagline: '24-port Gigabit PoE+ Catalyst 1000, 195 W budget, 4× 1G SFP uplinks',
    verified: false,
    datasheetUrl: 'https://www.cisco.com/c/en/us/products/collateral/switches/catalyst-1000-series-switches/nb-06-cat1k-ser-switch-ds-cte-en.html',
    specs: {
      totalPorts: 28,
      poePorts: 24,
      poeBudgetW: 195,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '4× 1G SFP',
      switchingCapacityGbps: 56,
      management: 'L2-managed',
      formFactor: 'rack-1U',
      fanless: true,
    },
  },

  // https://www.cisco.com/c/en/us/products/collateral/switches/catalyst-1000-series-switches/nb-06-cat1k-ser-switch-ds-cte-en.html (read 2026-05-14)
  {
    id: 'cisco-c1000-48p-4g-l',
    manufacturer: 'Cisco',
    model: 'C1000-48P-4G-L',
    category: 'poe-switch',
    tagline: '48-port Gigabit PoE+ Catalyst 1000, 370 W budget, 4× 1G SFP uplinks',
    verified: false,
    datasheetUrl: 'https://www.cisco.com/c/en/us/products/collateral/switches/catalyst-1000-series-switches/nb-06-cat1k-ser-switch-ds-cte-en.html',
    specs: {
      totalPorts: 52,
      poePorts: 48,
      poeBudgetW: 370,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '4× 1G SFP',
      switchingCapacityGbps: 104,
      management: 'L2-managed',
      formFactor: 'rack-1U',
      fanless: false,
    },
  },

  // https://www.cisco.com/c/en/us/products/collateral/switches/business-350-series-managed-switches/datasheet-c78-744156.html (read 2026-05-14 via reseller mirror; HTML blocks WebFetch)
  {
    id: 'cisco-cbs350-8p-e-2g',
    manufacturer: 'Cisco',
    model: 'CBS350-8P-E-2G',
    category: 'poe-switch',
    tagline: '8-port Gigabit PoE+ Business 350 L3, 67 W budget, 2× combo SFP — external PSU',
    verified: false,
    datasheetUrl: 'https://www.cisco.com/c/en/us/products/collateral/switches/business-350-series-managed-switches/datasheet-c78-744156.html',
    specs: {
      totalPorts: 10,
      poePorts: 8,
      poeBudgetW: 67,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '2× combo SFP',
      switchingCapacityGbps: 20,
      management: 'L3-managed',
      formFactor: 'desktop',
      fanless: true,
    },
  },

  // https://www.cisco.com/c/en/us/products/collateral/switches/business-350-series-managed-switches/datasheet-c78-744156.html (read 2026-05-14)
  {
    id: 'cisco-cbs350-24p-4g',
    manufacturer: 'Cisco',
    model: 'CBS350-24P-4G',
    category: 'poe-switch',
    tagline: '24-port Gigabit PoE+ Business 350 L3, 195 W budget, 4× 1G SFP uplinks',
    verified: false,
    datasheetUrl: 'https://www.cisco.com/c/en/us/products/collateral/switches/business-350-series-managed-switches/datasheet-c78-744156.html',
    specs: {
      totalPorts: 28,
      poePorts: 24,
      poeBudgetW: 195,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '4× 1G SFP',
      switchingCapacityGbps: 56,
      management: 'L3-managed',
      formFactor: 'rack-1U',
      fanless: false,
    },
  },

  // https://www.cisco.com/c/en/us/products/collateral/switches/business-350-series-managed-switches/datasheet-c78-744156.html (read 2026-05-14)
  {
    id: 'cisco-cbs350-48p-4g',
    manufacturer: 'Cisco',
    model: 'CBS350-48P-4G',
    category: 'poe-switch',
    tagline: '48-port Gigabit PoE+ Business 350 L3, 370 W budget, 4× 1G SFP uplinks',
    verified: false,
    datasheetUrl: 'https://www.cisco.com/c/en/us/products/collateral/switches/business-350-series-managed-switches/datasheet-c78-744156.html',
    specs: {
      totalPorts: 52,
      poePorts: 48,
      poeBudgetW: 370,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '4× 1G SFP',
      switchingCapacityGbps: 104,
      management: 'L3-managed',
      formFactor: 'rack-1U',
      fanless: false,
    },
  },

  // https://www.cisco.com/c/en/us/products/collateral/switches/business-250-series-smart-switches/nb-06-bus250-smart-switch-ds-cte-en.html (read 2026-05-14)
  {
    id: 'cisco-cbs250-8p-e-2g',
    manufacturer: 'Cisco',
    model: 'CBS250-8P-E-2G',
    category: 'poe-switch',
    tagline: '8-port Gigabit PoE+ Business 250 smart, 67 W budget, 2× combo SFP — fanless',
    verified: false,
    datasheetUrl: 'https://www.cisco.com/c/en/us/products/collateral/switches/business-250-series-smart-switches/nb-06-bus250-smart-switch-ds-cte-en.html',
    specs: {
      totalPorts: 10,
      poePorts: 8,
      poeBudgetW: 67,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '2× combo SFP',
      switchingCapacityGbps: 20,
      management: 'smart-managed',
      formFactor: 'desktop',
      fanless: true,
    },
  },

  // ─────────────────────────────────────────────────────────────
  // HPE Aruba — 2930F (Layer 3), 2930M, CX 6100, CX 6300M
  // ─────────────────────────────────────────────────────────────

  // https://www.hpe.com/psnow/doc/c05052929 (QuickSpecs; PDF/HTML access blocked, specs verified via reseller-hosted HPE datasheet PDF 2026-05-14)
  {
    id: 'hpe-aruba-2930f-8g-poe-jl258a',
    manufacturer: 'HPE Aruba',
    model: '2930F 8G PoE+ 2SFP+ (JL258A)',
    category: 'poe-switch',
    tagline: '8-port Gigabit PoE+ L3 managed, 139 W budget, 2× 1G/10G SFP+ uplinks',
    verified: false,
    datasheetUrl: 'https://www.hpe.com/psnow/doc/c05052929',
    specs: {
      totalPorts: 10,
      poePorts: 8,
      poeBudgetW: 139,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '2× 1G/10G SFP+',
      switchingCapacityGbps: 56,
      management: 'L3-managed',
      formFactor: 'rack-1U',
      fanless: false,
    },
  },

  // https://www.hpe.com/psnow/doc/c05052929 (read 2026-05-14)
  {
    id: 'hpe-aruba-2930f-48g-poe-jl262a',
    manufacturer: 'HPE Aruba',
    model: '2930F 48G PoE+ 4SFP (JL262A)',
    category: 'poe-switch',
    tagline: '48-port Gigabit PoE+ L3 managed, 370 W budget, 4× 1G SFP uplinks',
    verified: false,
    datasheetUrl: 'https://www.hpe.com/psnow/doc/c05052929',
    specs: {
      totalPorts: 52,
      poePorts: 48,
      poeBudgetW: 370,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '4× 1G SFP',
      switchingCapacityGbps: 176,
      management: 'L3-managed',
      formFactor: 'rack-1U',
      fanless: false,
    },
  },

  // https://www.hpe.com/psnow/doc/c05052929 (read 2026-05-14)
  {
    id: 'hpe-aruba-2930f-48g-poe-jl264a',
    manufacturer: 'HPE Aruba',
    model: '2930F 48G PoE+ 4SFP+ (JL264A)',
    category: 'poe-switch',
    tagline: '48-port Gigabit PoE+ L3 managed TAA, 370 W budget, 4× 10G SFP+ uplinks',
    verified: false,
    datasheetUrl: 'https://www.hpe.com/psnow/doc/c05052929',
    specs: {
      totalPorts: 52,
      poePorts: 48,
      poeBudgetW: 370,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '4× 10G SFP+',
      switchingCapacityGbps: 176,
      management: 'L3-managed',
      formFactor: 'rack-1U',
      fanless: false,
    },
  },

  // https://www.hpe.com/us/en/collaterals/collateral.a00004551enw.html (QuickSpecs; substituted from requested 24-port — JL322A is 48-port)
  {
    id: 'hpe-aruba-2930m-48g-poe-jl322a',
    manufacturer: 'HPE Aruba',
    model: '2930M 48G PoE+ 1-slot (JL322A)',
    category: 'poe-switch',
    tagline: '44-port Gigabit PoE+ + 4 combo L3 stackable, up to 1440 W with PSU, 1-slot uplink',
    verified: false,
    datasheetUrl: 'https://www.hpe.com/us/en/collaterals/collateral.a00004551enw.html',
    specs: {
      totalPorts: 48,
      poePorts: 48,
      poeBudgetW: 1440,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '4× combo RJ45/SFP + 1× expansion slot',
      switchingCapacityGbps: 176,
      management: 'L3-managed',
      formFactor: 'rack-1U',
      fanless: false,
    },
  },

  // https://www.hpe.com/psnow/doc/PSN1013152645TREN (read 2026-05-14 via search aggregation; HPE psnow PDF/HTML access timed out — specs verified against HPE/Aruba product page reseller mirror)
  {
    id: 'hpe-aruba-cx-6100-24g-poe-r8n87a',
    manufacturer: 'HPE Aruba',
    model: 'CX 6100 24G Class 4 PoE 4SFP+ (R8N87A)',
    category: 'poe-switch',
    tagline: '24-port Gigabit PoE+ CX 6100 L2, 370 W budget, 4× 10G SFP+ uplinks',
    verified: false,
    datasheetUrl: 'https://www.hpe.com/psnow/doc/PSN1013152645TREN',
    specs: {
      totalPorts: 28,
      poePorts: 24,
      poeBudgetW: 370,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '4× 10G SFP+',
      switchingCapacityGbps: 56,
      management: 'L2-managed',
      formFactor: 'rack-1U',
      fanless: false,
    },
  },

  // https://www.hpe.com/psnow/doc/PSN1012138130GREN (CX 6300 datasheet; substituted for non-existent R9Y04A — JL661A is the 6300M 48-port 1GbE PoE 4-port SFP56)
  {
    id: 'hpe-aruba-cx-6300m-48g-poe-jl661a',
    manufacturer: 'HPE Aruba',
    model: 'CX 6300M 48G Class 4 PoE 4SFP56 (JL661A)',
    category: 'poe-switch',
    tagline: '48-port Gigabit PoE+ CX 6300M L3 stackable, 1440 W budget, 4× 50G SFP56 uplinks',
    verified: false,
    datasheetUrl: 'https://www.hpe.com/psnow/doc/PSN1012138130GREN',
    specs: {
      totalPorts: 52,
      poePorts: 48,
      poeBudgetW: 1440,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '4× 1G/10G/25G/50G SFP56',
      switchingCapacityGbps: 496,
      management: 'L3-managed',
      formFactor: 'rack-1U',
      fanless: false,
    },
  },

  // ─────────────────────────────────────────────────────────────
  // Allied Telesis — GS950, x230 family, GS980MX
  // ─────────────────────────────────────────────────────────────

  // https://www.alliedtelesis.com/us/en/datasheet/gs9508poe (read 2026-05-14)
  {
    id: 'allied-telesis-gs950-8poe',
    manufacturer: 'Allied Telesis',
    model: 'AT-GS950/8POE',
    category: 'poe-switch',
    tagline: '8-port Gigabit web-smart, 4 PoE ports (802.3af), 61.6 W budget — fanless',
    verified: true,
    datasheetUrl: 'https://www.alliedtelesis.com/us/en/datasheet/gs9508poe',
    specs: {
      totalPorts: 8,
      poePorts: 4,
      poeBudgetW: 61.6,
      poeStandard: '802.3af',
      poePerPortW: 15.4,
      uplinks: '2× combo SFP',
      switchingCapacityGbps: 16,
      management: 'smart-managed',
      formFactor: 'desktop',
      fanless: true,
    },
  },

  // https://www.alliedtelesis.com/us/en/products/switches/x230-series/x230-28gp (read 2026-05-14)
  // Substituted for the requested x230L-26GP — x230L line is non-PoE; the
  // closest 24-PoE+ Allied Telesis SKU is x230-28GP.
  {
    id: 'allied-telesis-x230-28gp',
    manufacturer: 'Allied Telesis',
    model: 'AT-x230-28GP',
    category: 'poe-switch',
    tagline: '24-port Gigabit PoE+ L3 enterprise edge, 370 W budget, 4× SFP uplinks',
    verified: true,
    datasheetUrl: 'https://www.alliedtelesis.com/us/en/products/switches/x230-series/x230-28gp',
    specs: {
      totalPorts: 28,
      poePorts: 24,
      poeBudgetW: 370,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '4× SFP',
      switchingCapacityGbps: 56,
      management: 'L3-managed',
      formFactor: 'rack-1U',
      fanless: false,
    },
  },

  // https://www.alliedtelesis.com/us/en/products/switches/gs980mx-series/gs980mx10hsm (read 2026-05-14)
  // Substituted for the requested GS980EM/10HSm — that exact SKU does not
  // exist; the actual 10-port multi-gig 90 W/port unit is GS980MX/10HSm.
  {
    id: 'allied-telesis-gs980mx-10hsm',
    manufacturer: 'Allied Telesis',
    model: 'AT-GS980MX/10HSm',
    category: 'poe-switch',
    tagline: '8-port multi-gig PoE++ L3-Lite, 500 W budget, 90 W/port, 2× 10G SFP+ uplinks',
    verified: true,
    datasheetUrl: 'https://www.alliedtelesis.com/us/en/products/switches/gs980mx-series/gs980mx10hsm',
    specs: {
      totalPorts: 10,
      poePorts: 8,
      poeBudgetW: 500,
      poeStandard: '802.3bt-Type4',
      poePerPortW: 90,
      uplinks: '2× 10G SFP+',
      switchingCapacityGbps: 120,
      management: 'L3-managed',
      formFactor: 'rack-1U',
      fanless: false,
    },
  },

  // Milesight 4-Port PoE Switch datasheet (V2.02):
  // https://www.milesight.com/static/file/en/download/datasheet/poe-switch/Milesight-4-Port-PoE-Switch-Datasheet-en.pdf
  // "4X10/100Mbps PoE ports (RJ45) + 2X100Mbps uplink ports"; "IEEE802.3af/at";
  // "Max. capacity: 65W", "Max. capacity for single port: 30W"; "1.6Gbps
  // (non-blocking)"; "Extend Mode: Port 1~4: 10Mbps @250 meters"; "-20℃~55℃";
  // "Port lightning protection: 4KV"; "IP30"; "142.5mm(W)X115mm(D)X40mm(H)";
  // "Desktop Mounted Type/Wall Mounted Type". Product page
  // (milesight.com/product/poe-switch): "fanless design", "plug and play with
  // no configuration".
  {
    id: 'milesight-ms-s0204-el',
    manufacturer: 'Milesight',
    model: 'MS-S0204-EL',
    category: 'poe-switch',
    tagline: '4-port 10/100 PoE+ unmanaged, 65 W budget, 2 uplinks, 250 m extend mode',
    verified: true,
    datasheetUrl: 'https://www.milesight.com/static/file/en/download/datasheet/poe-switch/Milesight-4-Port-PoE-Switch-Datasheet-en.pdf',
    specs: {
      totalPorts: 6,
      poePorts: 4,
      poeBudgetW: 65,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '2× RJ45 100 Mbps',
      switchingCapacityGbps: 1.6,
      management: 'unmanaged',
      formFactor: 'desktop',
      fanless: true,
      operatingTempC: '-20 to 55',
      surgeProtectionKV: 4,
    },
  },
];
